<template lang="pug">
.workflow-breadcrumb.mb-4
  .card.shadow-sm
    .card-body.p-3
      .d-flex.justify-content-between.align-items-center.flex-wrap
        .workflow-step(
          v-for="(step, index) in steps"
          :key="index"
          :class="getStepClass(step)"
        )
          .step-indicator
            i.fas(:class="step.icon")
          .step-content
            .step-number {{ index + 1 }}
            .step-title {{ step.title }}
            .step-status(v-if="step.completed")
              i.fas.fa-check-circle.text-success
          .step-arrow(v-if="index < steps.length - 1")
            i.fas.fa-chevron-right
            
      //- Mostrar advertencias si faltan pasos
      .alert.alert-warning.mt-3(v-if="missingSteps.length > 0")
        .d-flex.align-items-start
          i.fas.fa-exclamation-triangle.me-2.mt-1
          div
            strong {{ $I18n.t('vue.order_workflow.missing_steps_title') }}
            ul.mb-0.mt-2
              li(v-for="step in missingSteps" :key="step") {{ step }}
</template>

<script>
export default {
  name: 'OrderWorkflowBreadcrumb',
  
  props: {
    orderState: {
      type: String,
      required: true
    },
    hasDueDate: {
      type: Boolean,
      default: false
    },
    hasScheduledDate: {
      type: Boolean,
      default: false
    },
    hasServices: {
      type: Boolean,
      default: false
    },
    hasInvoice: {
      type: Boolean,
      default: false
    },
    hasPayment: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    steps() {
      return [
        {
          key: 'created',
          title: this.$I18n.t('vue.order_workflow.step_created'),
          icon: 'fa-plus-circle',
          completed: true,
          required: true
        },
        {
          key: 'due',
          title: this.$I18n.t('vue.order_workflow.step_due'),
          icon: 'fa-calendar-alt',
          completed: this.hasDueDate,
          required: true
        },
        {
          key: 'scheduled',
          title: this.$I18n.t('vue.order_workflow.step_scheduled'),
          icon: 'fa-clock',
          completed: this.hasScheduledDate,
          required: true
        },
        {
          key: 'service',
          title: this.$I18n.t('vue.order_workflow.step_service'),
          icon: 'fa-tools',
          completed: this.hasServices,
          required: true,
          description: this.$I18n.t('vue.order_workflow.step_service_desc')
        },
        {
          key: 'completed',
          title: this.$I18n.t('vue.order_workflow.step_completed'),
          icon: 'fa-check-double',
          completed: this.orderState === 'COMPLETED',
          required: true
        },
        {
          key: 'invoiced',
          title: this.$I18n.t('vue.order_workflow.step_invoiced'),
          icon: 'fa-file-invoice',
          completed: this.hasInvoice,
          required: true
        },
        {
          key: 'paid',
          title: this.$I18n.t('vue.order_workflow.step_paid'),
          icon: 'fa-dollar-sign',
          completed: this.hasPayment,
          required: false
        }
      ];
    },
    
    missingSteps() {
      const missing = [];
      
      if (!this.hasDueDate) {
        missing.push(this.$I18n.t('vue.order_workflow.missing_due_date'));
      }
      if (!this.hasScheduledDate) {
        missing.push(this.$I18n.t('vue.order_workflow.missing_schedule'));
      }
      if (!this.hasServices) {
        missing.push(this.$I18n.t('vue.order_workflow.missing_services'));
      }
      if (this.orderState !== 'COMPLETED' && this.hasScheduledDate) {
        missing.push(this.$I18n.t('vue.order_workflow.missing_completion'));
      }
      
      return missing;
    }
  },
  
  methods: {
    getStepClass(step) {
      return {
        'step-completed': step.completed,
        'step-pending': !step.completed && step.required,
        'step-optional': !step.completed && !step.required
      };
    }
  }
}
</script>

<style scoped>
.workflow-breadcrumb {
  font-size: 0.9rem;
}

.workflow-step {
  position: relative;
  flex: 1;
  min-width: 120px;
  padding: 0.5rem;
  text-align: center;
  transition: all 0.3s ease;
}

.step-indicator {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.step-content {
  position: relative;
}

.step-number {
  display: inline-block;
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  font-weight: bold;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.step-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: #495057;
  margin-top: 0.25rem;
}

.step-status {
  margin-top: 0.25rem;
  font-size: 1.2rem;
}

.step-arrow {
  position: absolute;
  right: -15px;
  top: 50%;
  transform: translateY(-50%);
  color: #dee2e6;
  font-size: 1.2rem;
  z-index: 1;
}

/* Estados de los pasos */
.step-completed {
  opacity: 1;
}

.step-completed .step-indicator {
  color: #28a745;
}

.step-completed .step-number {
  background-color: #28a745;
  color: white;
}

.step-completed .step-title {
  color: #28a745;
  font-weight: 700;
}

.step-pending {
  opacity: 0.6;
}

.step-pending .step-indicator {
  color: #ffc107;
}

.step-pending .step-number {
  background-color: #ffc107;
  color: white;
}

.step-optional {
  opacity: 0.4;
}

.step-optional .step-indicator {
  color: #6c757d;
}

/* Responsive */
@media (max-width: 768px) {
  .workflow-step {
    min-width: 80px;
    padding: 0.25rem;
  }
  
  .step-indicator {
    font-size: 1.2rem;
  }
  
  .step-title {
    font-size: 0.7rem;
  }
  
  .step-arrow {
    font-size: 0.9rem;
    right: -10px;
  }
}
</style>
